// app/ningenie/page.tsx
'use client'

// baby: NinGenie UI with Firebase integration
import { memo, useState, useCallback, useMemo, useRef, useEffect, Suspense } from 'react'
import { useSearchParams } from 'next/navigation'
import { Montserrat_Underline } from '@/app/fonts/google-font-fallbacks'
import dynamic from 'next/dynamic'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { auth, onAuthStateChanged } from '@/app/lib/firebase'
import type { User } from 'firebase/auth'
import { signOutUser } from '@/app/lib/firebase'
import { isUsingMockData } from './lib/ningenie-user'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem
} from '@/components/ui/dropdown-menu'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupButton
} from '@/components/ui/input-group'
import { ChevronDown, MoreHorizontal, Search, Sparkles, Bot, Plus, List, Clock, FileText, CreditCard, Tag, Sun, Moon, HelpCircle, Settings, BookOpen, LogOut, AlertCircle, X, Plug, AudioLines, MessageCircle, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useNinGenieSnapshot, useInvalidateNinGenieSnapshot } from './hooks/use-ningenie-snapshot'
import type { NinGenieTransaction, NinGenieAccount, NinGenieBudget } from './lib/ningenie-types'

// baby: dynamically import Lottie to avoid SSR issues
const Lottie = dynamic(() => import('lottie-react'), { ssr: false })

const TabPanelLoader = () => (
  <div className="flex min-h-[220px] items-center justify-center text-sm text-muted-foreground">
    Loading section...
  </div>
)

const DrawerLoader = () => (
  <div className="flex min-h-[160px] items-center justify-center text-sm text-muted-foreground">
    Loading...
  </div>
)

// baby: money sections are only needed once the user opens them, so split them out.
const HistoryTab = dynamic(
  () => import('./components/HistoryTab').then((m) => ({ default: m.HistoryTab })),
  { ssr: false, loading: () => <TabPanelLoader /> }
)

const AnalysisTab = dynamic(
  () => import('./components/AnalysisTab').then((m) => ({ default: m.AnalysisTab })),
  { ssr: false, loading: () => <TabPanelLoader /> }
)

const AccountsTab = dynamic(
  () => import('./components/AccountsTab').then((m) => ({ default: m.AccountsTab })),
  { ssr: false, loading: () => <TabPanelLoader /> }
)

const CategoriesTab = dynamic(
  () => import('./components/CategoriesTab').then((m) => ({ default: m.CategoriesTab })),
  { ssr: false, loading: () => <TabPanelLoader /> }
)

const PluginsTab = dynamic(
  () => import('./components/PluginsTab').then((m) => ({ default: m.PluginsTab })),
  { ssr: false, loading: () => <TabPanelLoader /> }
)

const BudgetTab = dynamic(
  () => import('./components/BudgetTab').then((m) => ({ default: m.BudgetTab })),
  { ssr: false, loading: () => <TabPanelLoader /> }
)

const DirectoryTab = dynamic(
  () => import('./components/DirectoryTab').then((m) => ({ default: m.DirectoryTab })),
  { ssr: false, loading: () => <TabPanelLoader /> }
)

const AddTransactionDrawer = dynamic(
  () => import('./components/AddTransactionDrawer').then((m) => ({ default: m.AddTransactionDrawer })),
  { ssr: false, loading: () => <DrawerLoader /> }
)

const DatePickerDialog = dynamic(
  () => import('./components/DatePickerDialog').then((m) => ({ default: m.DatePickerDialog })),
  { ssr: false, loading: () => <DrawerLoader /> }
)

const SignInDrawer = dynamic(
  () => import('./components/SignInDrawer').then((m) => ({ default: m.SignInDrawer })),
  { ssr: false, loading: () => <DrawerLoader /> }
)

const NinGenieChatDrawer = dynamic(
  () => import('./components/NinGenieChatDrawer').then((m) => ({ default: m.NinGenieChatDrawer })),
  { ssr: false, loading: () => <DrawerLoader /> }
)

const SettingsDrawer = dynamic(
  () => import('./components/SettingsDrawer').then((m) => ({ default: m.SettingsDrawer })),
  { ssr: false, loading: () => <DrawerLoader /> }
)

const NinGenieDesktopHomeScreen = dynamic(
  () => import('./components/desktop/NinGenieDesktopHomeScreen').then((m) => ({ default: m.NinGenieDesktopHomeScreen })),
  { ssr: false, loading: () => <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Loading workspace...</div> }
)

// baby: fetch animation data function for React Query
const fetchChatButtonAnimationData = async (): Promise<Record<string, unknown>> => {
  const response = await fetch('/jsons/chat_ai_animation.json')
  if (!response.ok) {
    throw new Error(`Failed to load chat AI animation: ${response.status}`)
  }
  return response.json()
}

// baby: Montserrat Subrayada font for NINGenie branding
const montserratSubrayada = Montserrat_Underline({
  weight: ['400', '700'],
  subsets: ['latin'],
  display: 'swap',
  // baby: No capsize metrics for "Montserrat Underline" key in Next.js (legacy: Subrayada); skip fallback CSS tuning
  adjustFontFallback: false,
})

const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2
})

const NinGenieHomeScreenComponent = () => {
  const searchParams = useSearchParams()
  const { data: snapshot, isLoading } = useNinGenieSnapshot()
  const invalidateSnapshot = useInvalidateNinGenieSnapshot()
  const [isAddTransactionOpen, setIsAddTransactionOpen] = useState(false)
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null })
  const [activeTab, setActiveTab] = useState<'history' | 'analysis' | 'budget' | 'accounts' | 'categories' | 'plugins' | 'directory'>('plugins')
  const [searchQuery, setSearchQuery] = useState('')
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isSignInDrawerOpen, setIsSignInDrawerOpen] = useState(false)
  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [showMockDataBanner, setShowMockDataBanner] = useState(false)
  const [shouldLoadPrimaryTabs, setShouldLoadPrimaryTabs] = useState(false)
  
  // baby: Check for OAuth success callback
  const pluginParam = searchParams?.get('plugin')
  const successParam = searchParams?.get('success')
  const emailParam = searchParams?.get('email')
  const calendarOAuthParam = searchParams?.get('calendar_oauth')
  const isGmailOAuthSuccess = pluginParam === 'gmail' && successParam === 'true'
  const isCalendarOAuthSuccess = pluginParam === 'calendar' && calendarOAuthParam === 'success'
  const isConnectPlatformCallback =
    searchParams?.get('connected') === 'true' &&
    Boolean(searchParams?.get('status')) &&
    Boolean(pluginParam)
  const isOAuthSuccess = isGmailOAuthSuccess || isCalendarOAuthSuccess
  const [showOAuthSuccessDialog, setShowOAuthSuccessDialog] = useState(false)
  const [oauthSuccessType, setOauthSuccessType] = useState<'gmail' | 'calendar' | null>(null)
  // baby: lazy initialize darkMode from localStorage (avoids useEffect)
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme')
      if (savedTheme === 'dark') {
        // baby: apply theme immediately to avoid flash
        document.documentElement.classList.add('dark')
        document.documentElement.classList.remove('light')
        return true
      }
      if (savedTheme === 'light') {
        document.documentElement.classList.remove('dark')
        document.documentElement.classList.add('light')
        return false
      }
    }
    return false
  })
  const prevUserIdRef = useRef<string | null>(null)
  const hasAutoOpenedChatRef = useRef(false)
  // baby: use React Query to load animation data (avoids useEffect for data loading)
  const { data: chatAnimationData } = useQuery({
    queryKey: ['ningenie', 'chat-button', 'animation'],
    queryFn: fetchChatButtonAnimationData,
    staleTime: Number.POSITIVE_INFINITY, // baby: static JSON, never stale
    gcTime: 1000 * 60 * 60 // baby: cache for 1 hour
  })
  const lottieRef = useRef<any>(null)
  const [scrollY, setScrollY] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // baby: auto-play animation on load and every 30 seconds
  useEffect(() => {
    if (!chatAnimationData) return

    // baby: play animation function - starts from beginning
    const playAnimation = () => {
      if (lottieRef.current) {
        try {
          // baby: restart animation from beginning (frame 0)
          lottieRef.current.goToAndPlay(0)
        } catch (error) {
          console.error('[NinGenie] Failed to play animation:', error)
        }
      }
    }

    // baby: set initial frame to middle frame (900) when animation loads
    const setInitialFrame = () => {
      if (lottieRef.current) {
        try {
          // baby: go to middle frame (900) and stop - this is the thumbnail
          lottieRef.current.goToAndStop(900, true)
        } catch (error) {
          console.error('[NinGenie] Failed to set initial frame:', error)
        }
      }
    }

    // baby: wait for Lottie component to mount and ref to be set, then set initial frame
    const initialTimeout = setTimeout(() => {
      setInitialFrame()
      // baby: play animation after setting initial frame
      setTimeout(() => {
        playAnimation()
      }, 100)
    }, 300)

    // baby: set up interval to play every 20 seconds
    const interval = setInterval(() => {
      playAnimation()
    }, 30000) // 30 seconds

    return () => {
      clearTimeout(initialTimeout)
      clearInterval(interval)
    }
  }, [chatAnimationData])

  // baby: Show OAuth success dialog when callback is detected
  useEffect(() => {
    if (isGmailOAuthSuccess) {
      setOauthSuccessType('gmail')
      setShowOAuthSuccessDialog(true)
      // baby: Clean up URL params after showing dialog
      const url = new URL(window.location.href)
      url.searchParams.delete('plugin')
      url.searchParams.delete('success')
      url.searchParams.delete('email')
      window.history.replaceState({}, '', url.toString())
    } else if (isCalendarOAuthSuccess) {
      setOauthSuccessType('calendar')
      setShowOAuthSuccessDialog(true)
      // baby: Clean up URL params after showing dialog
      const url = new URL(window.location.href)
      url.searchParams.delete('plugin')
      url.searchParams.delete('calendar_oauth')
      url.searchParams.delete('message')
      window.history.replaceState({}, '', url.toString())
    }
  }, [isGmailOAuthSuccess, isCalendarOAuthSuccess])

  // baby: auto-open chat drawer on first page load/refresh (skip if noDrawer query param is present)
  useEffect(() => {
    const skipDrawer = searchParams?.get('noDrawer') === 'true'
    // baby: Don't auto-open chat drawer if OAuth success callback is detected
    if (!hasAutoOpenedChatRef.current && user && !skipDrawer && !isGmailOAuthSuccess && !isCalendarOAuthSuccess && !isConnectPlatformCallback) {
      // baby: small delay to ensure page is fully loaded
      const timer = setTimeout(() => {
        setIsChatDrawerOpen(true)
        hasAutoOpenedChatRef.current = true
      }, 300)
      return () => clearTimeout(timer)
    } else if (skipDrawer) {
      // baby: mark as opened even if skipped to prevent reopening
      hasAutoOpenedChatRef.current = true
    }
  }, [user, searchParams, isGmailOAuthSuccess, isCalendarOAuthSuccess, isConnectPlatformCallback])

  useEffect(() => {
    const skipDrawer = searchParams?.get('noDrawer') === 'true'
    if (skipDrawer || !isChatDrawerOpen) {
      setShouldLoadPrimaryTabs(true)
    }
  }, [isChatDrawerOpen, searchParams])

  // baby: get current user and check if using mock data
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null
    
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      const currentUserId = currentUser?.uid || null
      const prevUserId = prevUserIdRef.current
      
      // baby: invalidate and refetch data when user changes (sign in/out)
      if (prevUserId !== currentUserId) {
        console.log('[NinGenie] Auth state changed, invalidating snapshot data', { prevUserId, currentUserId })
        prevUserIdRef.current = currentUserId
        invalidateSnapshot()
      }
      
      setUser(currentUser)
      
      // baby: show mock data banner if not authenticated
      if (!currentUser) {
        setShowMockDataBanner(true)
        // baby: auto-open sign-in drawer after a delay if not authenticated
        timer = setTimeout(() => {
          setIsSignInDrawerOpen(true)
        }, 2000) // Show drawer after 2 seconds
      } else {
        setShowMockDataBanner(false)
        if (timer) clearTimeout(timer)
      }
    })
    
    return () => {
      unsubscribe()
      if (timer) clearTimeout(timer)
    }
  }, [invalidateSnapshot])

  // baby: truncate email to fit "Phone Number" button size
  const displayEmail = useMemo(() => {
    if (!user?.email) return 'Sign In'
    const email = user.email
    const maxLength = 12 // approximate "Phone Number" length
    if (email.length <= maxLength) return email
    return `${email.slice(0, maxLength)}...`
  }, [user])

  // baby: apply theme changes (only runs when darkMode changes, not on mount)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (darkMode) {
        document.documentElement.classList.add('dark')
        document.documentElement.classList.remove('light')
        localStorage.setItem('theme', 'dark')
      } else {
        document.documentElement.classList.remove('dark')
        document.documentElement.classList.add('light')
        localStorage.setItem('theme', 'light')
      }
      
      // Dispatch theme change event
      window.dispatchEvent(
        new CustomEvent('themeChange', { detail: { isDark: darkMode } })
      )
    }
  }, [darkMode])
  
  const handleHelp = useCallback(() => {
    setIsHelpOpen(true)
    // baby: placeholder for help dialog/modal
    console.log('Help clicked')
  }, [])
  
  const handleSettings = useCallback(() => {
    setIsSettingsOpen(true)
    // baby: placeholder for settings dialog/modal
    console.log('Settings clicked')
  }, [])
  
  // baby: track scroll position for collapsing summary card
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      setScrollY(container.scrollTop)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])
  
  // baby: calculate summary card collapse state (collapses when scrolling up)
  const summaryCardCollapsed = scrollY > 50
  const summaryCardHeight = summaryCardCollapsed ? 80 : 200 // collapsed vs expanded height

  // baby: format period label - show "ALL TIME" for accounts/categories tabs, show month for budget tab
  const periodLabel = useMemo(() => {
    if (activeTab === 'budget') {
      const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
      const monthName = monthNames[selectedDate.getMonth()]
      const shortMonthName = monthName.slice(0, 3).charAt(0).toUpperCase() + monthName.slice(1, 3)
      return `${shortMonthName} ${selectedDate.getFullYear().toString().slice(-2)}`.toUpperCase()
    }
    
    if (activeTab !== 'history' && activeTab !== 'analysis') {
      return 'ALL TIME'
    }

    if (dateRange.start && dateRange.end) {
      const startStr = dateRange.start.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
      const endStr = dateRange.end.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
      if (startStr === endStr) {
        return startStr
      }
      return `${startStr} - ${endStr}`
    }
    return selectedDate.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
  }, [selectedDate, dateRange, activeTab])

  // baby: calculate summary - total balance calculation depends on tab
  const filteredSummary = useMemo(() => {
    if (!snapshot) {
      return {
        totalIncome: 0,
        totalExpense: 0,
        totalBalance: 0,
        totalBudget: 0,
        totalSpent: 0,
        periodLabel
      }
    }

    // baby: budget tab shows budget summary
    if (activeTab === 'budget') {
      const selectedYear = selectedDate.getFullYear()
      const selectedMonth = selectedDate.getMonth()
      const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
      const monthName = monthNames[selectedMonth]
      const shortMonthName = monthName.slice(0, 3).charAt(0).toUpperCase() + monthName.slice(1, 3)
      const budgetPeriodLabel = `${shortMonthName} ${selectedYear.toString().slice(-2)}`

      const monthBudgets = snapshot.budgets.filter((budget: NinGenieBudget) => 
        budget.periodLabel === budgetPeriodLabel && budget.period === 'monthly'
      )

      const totalBudget = monthBudgets.reduce((acc: number, budget: NinGenieBudget) => acc + budget.limit, 0)
      const totalSpent = monthBudgets.reduce((acc: number, budget: NinGenieBudget) => acc + budget.spent, 0)

      return {
        totalIncome: 0,
        totalExpense: 0,
        totalBalance: 0,
        totalBudget,
        totalSpent,
        periodLabel
      }
    }

    let filteredTransactions = snapshot.transactions

    // baby: filter by date if in history or analysis tab
    if (activeTab === 'history' || activeTab === 'analysis') {
      if (dateRange.start && dateRange.end) {
        // Filter by month range
        filteredTransactions = filteredTransactions.filter((txn: NinGenieTransaction) => {
          const txnDate = new Date(txn.timestamp)
          const txnYear = txnDate.getFullYear()
          const txnMonth = txnDate.getMonth()
          
          const startYear = dateRange.start!.getFullYear()
          const startMonth = dateRange.start!.getMonth()
          const endYear = dateRange.end!.getFullYear()
          const endMonth = dateRange.end!.getMonth()
          
          const txnMonthIndex = txnYear * 12 + txnMonth
          const startMonthIndex = startYear * 12 + startMonth
          const endMonthIndex = endYear * 12 + endMonth
          
          return txnMonthIndex >= startMonthIndex && txnMonthIndex <= endMonthIndex
        })
      } else {
        // Filter by selected month/year
        const selectedYear = selectedDate.getFullYear()
        const selectedMonth = selectedDate.getMonth()
        filteredTransactions = filteredTransactions.filter((txn: NinGenieTransaction) => {
          const txnDate = new Date(txn.timestamp)
          return txnDate.getFullYear() === selectedYear && txnDate.getMonth() === selectedMonth
        })
      }
    }
    // baby: for accounts/categories tabs, use Firebase summary field (all-time totals)
    // For history/analysis tabs, calculate from filtered transactions
    if (activeTab === 'accounts' || activeTab === 'categories') {
      // baby: use Firebase summary for all-time totals (more efficient than recalculating)
      const totalBalance = snapshot.accounts.reduce((acc: number, account: NinGenieAccount) => acc + account.balance, 0)
      
      return {
        totalIncome: snapshot.summary.totalIncome,
        totalExpense: snapshot.summary.totalExpense,
        totalBalance,
        totalBudget: 0,
        totalSpent: 0,
        periodLabel
      }
    }

    // baby: for history/analysis tabs, calculate from filtered transactions
    // baby: exclude transfers (transactions with counterpartyAccountId) from income/expense calculations
    const income = filteredTransactions
      .filter((txn: NinGenieTransaction) => 
        txn.type === 'income' && !txn.counterpartyAccountId // exclude transfers
      )
      .reduce((acc: number, txn: NinGenieTransaction) => acc + txn.amount, 0)

    const expense = filteredTransactions
      .filter((txn: NinGenieTransaction) => 
        txn.type === 'expense' && !txn.counterpartyAccountId // exclude transfers
      )
      .reduce((acc: number, txn: NinGenieTransaction) => acc + txn.amount, 0)

    // baby: total balance calculation for history tab
    const totalBalance = income - expense

    return {
      totalIncome: income,
      totalExpense: expense,
      totalBalance,
      totalBudget: 0,
      totalSpent: 0,
      periodLabel
    }
  }, [snapshot, selectedDate, dateRange, periodLabel, activeTab])

  const summary = filteredSummary

  const handleAddTransaction = useCallback(() => {
    setIsAddTransactionOpen(true)
  }, [])

  // baby: chat drawer is now opened via button onClick

  return (
    <div className="relative mx-auto flex w-full max-w-md flex-col h-screen overflow-hidden bg-background">
      {/* Mock Data Banner */}
      {showMockDataBanner && !user && (
        <div className="sticky top-0 z-40 px-4 pt-2 pb-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800">
          <div className="flex items-center justify-between gap-2 rounded-2xl bg-amber-100 dark:bg-amber-900/50 border border-amber-300 dark:border-amber-700 p-3">
            <div className="flex items-start gap-2 flex-1">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">
                  Viewing Mock Data
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Sign in to save and sync your own financial data
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSignInDrawerOpen(true)}
              className="h-7 rounded-full bg-amber-200 dark:bg-amber-800 hover:bg-amber-300 dark:hover:bg-amber-700 text-amber-900 dark:text-amber-100 text-xs font-semibold"
            >
              Sign In
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full flex-shrink-0"
              onClick={() => setShowMockDataBanner(false)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Fixed Header */}
      <header className={cn(
        "flex items-center justify-between px-4 pt-6 pb-4 bg-background z-30 sticky",
        showMockDataBanner && !user ? "top-[72px]" : "top-0"
      )}>
        <div className="space-y-3">
          <div className="space-y-1">
            <span className={cn("text-xs ml-2 font-semibold tracking-[0.3em] text-muted-foreground", montserratSubrayada.className)}>
              NINGENIE
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full border-border/60 bg-accent/10 text-sm font-semibold"
                title={user?.email || 'Not signed in - Click to sign in'}
                onClick={() => !user && setIsSignInDrawerOpen(true)}
              >
                <span className="truncate max-w-[120px]">{displayEmail}</span>
                <ChevronDown className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              </Button>
              {!user && (
                <Badge
                  variant="secondary"
                  className="rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 cursor-pointer hover:bg-amber-500/20"
                  onClick={() => setIsSignInDrawerOpen(true)}
                  role="button"
                  tabIndex={0}
                >
                  Mock Data
                </Badge>
              )}
              {user && (
                <Badge
                  variant="secondary"
                  className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"
                >
                  {/* Agentic */}
                </Badge>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            NinGenie keeps a close watch on money stories in real time.
            {!user && (
              <span className="text-amber-600 dark:text-amber-400 font-semibold ml-1">
                (Viewing mock data - Sign in to save your data)
              </span>
            )}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full border border-border/60"
              aria-label="NinGenie quick actions"
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-[var(--background)]">
            <DropdownMenuLabel>Options</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {/* Theme Toggle */}
            <DropdownMenuRadioGroup value={darkMode ? 'dark' : 'light'} onValueChange={(value) => setDarkMode(value === 'dark')}>
              <DropdownMenuLabel className="px-2 py-1.5 text-xs font-normal text-muted-foreground">
                Theme
              </DropdownMenuLabel>
              <DropdownMenuRadioItem value="light" className="cursor-pointer">
                <Sun className="mr-2 h-4 w-4" />
                <span>Light</span>
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark" className="cursor-pointer">
                <Moon className="mr-2 h-4 w-4" />
                <span>Dark</span>
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            
            <DropdownMenuSeparator />
            
            {/* Help - Telegram */}
            <DropdownMenuItem asChild>
              <a
                href="https://t.me/+zxixB1S99rIzYWY1"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                <HelpCircle className="mr-2 h-4 w-4" />
                <span>Help (Telegram)</span>
              </a>
            </DropdownMenuItem>

            {/* WhatsApp */}
            <DropdownMenuItem asChild>
              <a
                href="https://wa.me/917337557707"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                <span>WhatsApp Support</span>
              </a>
            </DropdownMenuItem>
            
            {/* Settings */}
            <DropdownMenuItem onClick={handleSettings} className="cursor-pointer">
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            {/* Sign Out */}
            <DropdownMenuItem 
              onClick={async () => {
                try {
                  await signOutUser()
                  // baby: redirect will happen automatically via AuthProvider
                  window.location.reload()
                } catch (error) {
                  console.error('Sign out error:', error)
                }
              }} 
              className="cursor-pointer text-rose-500 focus:text-rose-600 focus:bg-rose-50 dark:focus:bg-rose-950/20"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sign Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Fixed Summary Card - collapses on scroll */}
      <div 
        className="px-4 pb-4 bg-background z-30 sticky transition-all duration-300 ease-in-out"
        style={{ 
          top: showMockDataBanner && !user 
            ? '192px' // below header (120px) and banner (72px)
            : '120px', // below header only
          height: `${summaryCardHeight}px`,
          overflow: 'hidden'
        }}
      >
        <Card className="rounded-3xl border border-border/60 light:bg-gradient-to-br from-sky-50 via-white to-slate-50 text-slate-900 shadow-xm dark:border-[#88a1ac18] dark:bg-[#88a1ac18] dark:text-slate-50">
          <CardContent className={cn(
            "space-y-6 p-6 transition-all duration-300",
            summaryCardCollapsed && "space-y-2 p-4"
          )}>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">Loading...</div>
            </div>
          ) : (
            <>
              {/* Expanded Summary View */}
              <div className={cn(
                "flex items-center justify-between transition-all duration-300",
                summaryCardCollapsed && "opacity-0 h-0 overflow-hidden pointer-events-none"
              )}>
                <div className="space-y-1 text-center">
                  {activeTab === 'budget' ? (
                    <>
                      <p className="text-xs font-medium text-muted-foreground">Total Budget</p>
                      <p className="text-sm font-semibold text-emerald-600">
                        {inrFormatter.format(summary.totalBudget)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-medium text-muted-foreground">Income</p>
                      <p className="text-sm font-semibold text-emerald-600">
                        {inrFormatter.format(summary.totalIncome)}
                      </p>
                    </>
                  )}
                </div>
                <div className="space-y-1 text-center">
                  {activeTab === 'budget' ? (
                    <>
                      <p className="text-xs font-medium text-muted-foreground">Total Spent</p>
                      <p className={cn('text-sm font-semibold', summary.totalSpent > summary.totalBudget ? 'text-rose-600' : 'text-foreground')}>
                        {inrFormatter.format(summary.totalSpent)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-medium text-muted-foreground">Expense</p>
                      <p className="text-sm font-semibold text-rose-600">
                        {inrFormatter.format(summary.totalExpense)}
                      </p>
                    </>
                  )}
                </div>
                <div className="space-y-1 text-center">
                  {activeTab === 'budget' ? (
                    <>
                      <p className="text-xs font-medium text-muted-foreground">Remaining</p>
                      <p className={cn('text-sm font-semibold', summary.totalSpent > summary.totalBudget ? 'text-rose-600' : 'text-foreground')}>
                        {inrFormatter.format(Math.max(0, summary.totalBudget - summary.totalSpent))}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-medium text-muted-foreground">Total</p>
                      <p className="text-sm font-semibold">
                        {inrFormatter.format(summary.totalBalance)}
                      </p>
                    </>
                  )}
                </div>
              </div>
              
              {/* Collapsed Summary View */}
              <div className={cn(
                "flex items-center justify-center gap-4 transition-all duration-300",
                !summaryCardCollapsed && "opacity-0 h-0 overflow-hidden pointer-events-none"
              )}>
                <div className="text-center">
                  {activeTab === 'budget' ? (
                    <p className="text-xs font-semibold">
                      {inrFormatter.format(summary.totalBudget)} Budget • {inrFormatter.format(summary.totalSpent)} Spent
                    </p>
                  ) : (
                    <p className="text-xs font-semibold">
                      <span className="text-emerald-600">{inrFormatter.format(summary.totalIncome)} Income</span> • <span className="text-rose-600">{inrFormatter.format(summary.totalExpense)} Expense</span>
                    </p>
                  )}
                </div>
              </div>
              
              <div className={cn(
                "flex items-center justify-between transition-all duration-300",
                summaryCardCollapsed && "opacity-0 h-0 overflow-hidden pointer-events-none"
              )}>
                <Button
                  variant="outline"
                  onClick={() => (activeTab === 'history' || activeTab === 'analysis' || activeTab === 'budget') && setIsDatePickerOpen(true)}
                  disabled={activeTab !== 'history' && activeTab !== 'analysis' && activeTab !== 'budget'}
                  className={`rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1.5 ${
                    activeTab !== 'history' && activeTab !== 'analysis' && activeTab !== 'budget' ? 'opacity-50 cursor-not-allowed' : 'bg-primary hover:bg-primary/90'
                  }`}
                >
                  {periodLabel}
                  <ChevronDown className="h-3 w-3 opacity-70" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 rounded-full bg-primary/10 px-3 text-xs font-semibold text-primary"
                >
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                  Smart summary ready
                </Button>
              </div>
            </>
          )}
        </CardContent>
        </Card>
      </div>

      {/* Scrollable Tabs and Content */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-4 pb-24 bg-transparent"
      >
        <Tabs defaultValue="plugins" className="w-full bg-transparent" onValueChange={(value) => setActiveTab(value as any)}>
        <div className="relative">
          <div className="-mx-2 overflow-x-auto px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsList className="flex w-max gap-2 rounded-full bg-transparent p-1 m-0.5">
              <TabsTrigger
                value="plugins"
                className={cn(
                  'rounded-full border border-border/60 text-base font-semibold transition-all opacity-70 flex items-center gap-2 px-4 py-2',
                  'data-[state=active]:bg-[#21728d1f] data-[state=active]:border-2 data-[state=active]:border-[#21728d] data-[state=active]:text-[#21728d] data-[state=active]:opacity-100'
                )}
              >
                <Plug className="h-5 w-5" aria-hidden="true" />
                Plugins
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className={cn(
                  'rounded-full border border-border/60 text-base font-semibold transition-all opacity-70 flex items-center gap-2 px-4 py-2',
                  'data-[state=active]:bg-[#21728d1f] data-[state=active]:border-2 data-[state=active]:border-[#21728d] data-[state=active]:text-[#21728d] data-[state=active]:opacity-100'
                )}
              >
                <List className="h-5 w-5" aria-hidden="true" />
                History
              </TabsTrigger>
              <TabsTrigger
                value="directory"
                className={cn(
                  'rounded-full border border-border/60 text-base font-semibold transition-all opacity-70 flex items-center gap-2 px-4 py-2',
                  'data-[state=active]:bg-[#21728d1f] data-[state=active]:border-2 data-[state=active]:border-[#21728d] data-[state=active]:text-[#21728d] data-[state=active]:opacity-100'
                )}
              >
                <BookOpen className="h-5 w-5" aria-hidden="true" />
                Directory
              </TabsTrigger>
              <TabsTrigger
                value="analysis"
                className={cn(
                  'rounded-full border border-border/60 text-base font-semibold transition-all opacity-70 flex items-center gap-2 px-4 py-2',
                  'data-[state=active]:bg-[#21728d1f] data-[state=active]:border-2 data-[state=active]:border-[#21728d] data-[state=active]:text-[#21728d] data-[state=active]:opacity-100'
                )}
              >
                <Clock className="h-5 w-5" aria-hidden="true" />
                Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="budget" 
                className={cn(
                  'rounded-full border border-border/60 text-base font-semibold transition-all opacity-70 flex items-center gap-2 px-4 py-2',
                  'data-[state=active]:bg-[#21728d1f] data-[state=active]:border-2 data-[state=active]:border-[#21728d] data-[state=active]:text-[#21728d] data-[state=active]:opacity-100'
                )}
              >
                <FileText className="h-5 w-5" aria-hidden="true" />
                Budget
              </TabsTrigger>
              <TabsTrigger 
                value="accounts" 
                className={cn(
                  'rounded-full border border-border/60 text-base font-semibold transition-all opacity-70 flex items-center gap-2 px-4 py-2',
                  'data-[state=active]:bg-[#21728d1f] data-[state=active]:border-2 data-[state=active]:border-[#21728d] data-[state=active]:text-[#21728d] data-[state=active]:opacity-100'
                )}
              >
                <CreditCard className="h-5 w-5" aria-hidden="true" />
                Accounts
              </TabsTrigger>
                 <TabsTrigger
                   value="categories"
                   className={cn(
                     'rounded-full border border-border/60 text-base font-semibold transition-all opacity-70 flex items-center gap-2 px-4 py-2',
                     'data-[state=active]:bg-[#21728d1f] data-[state=active]:border-2 data-[state=active]:border-[#21728d] data-[state=active]:text-[#21728d] data-[state=active]:opacity-100'
                   )}
                 >
                   <Tag className="h-5 w-5" aria-hidden="true" />
                   Categories
                 </TabsTrigger>
                 
               </TabsList>
          </div>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent" />
        </div>

        <TabsContent value="history" className="space-y-5 pt-4">
          {shouldLoadPrimaryTabs ? (
            <>
              <InputGroup className="h-11 rounded-full border border-border/60 dark:border-[#88a1ac18] bg-background">
                <InputGroupAddon className="pl-4">
                  <Search className="h-4 w-4" aria-hidden="true" />
                </InputGroupAddon>
                <InputGroupInput 
                  placeholder="Search notes, records" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <InputGroupAddon className="pr-2">
                  <InputGroupButton
                    size="sm"
                    variant="ghost"
                    className="rounded-full text-xs font-semibold"
                    onClick={() => setSearchQuery('')}
                  >
                    {searchQuery ? 'Clear' : 'All'}
                  </InputGroupButton>
                </InputGroupAddon>
              </InputGroup>

              <Button
                onClick={handleAddTransaction}
                variant="default"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-primary text-sm font-semibold text-primary-foreground"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add Transaction
              </Button>

              <HistoryTab 
                onAddTransaction={handleAddTransaction} 
                selectedDate={selectedDate} 
                dateRange={dateRange}
                searchQuery={searchQuery}
              />
            </>
          ) : (
            <TabPanelLoader />
          )}
        </TabsContent>

        <TabsContent value="analysis" className="pt-4">
          {shouldLoadPrimaryTabs ? <AnalysisTab selectedDate={selectedDate} dateRange={dateRange} /> : <TabPanelLoader />}
        </TabsContent>

        <TabsContent value="budget" className="pt-4">
          {shouldLoadPrimaryTabs ? <BudgetTab selectedDate={selectedDate} onDateChange={setSelectedDate} /> : <TabPanelLoader />}
        </TabsContent>

        <TabsContent value="accounts" className="pt-4">
          {shouldLoadPrimaryTabs ? <AccountsTab /> : <TabPanelLoader />}
        </TabsContent>

        <TabsContent value="categories" className="pt-4">
          {shouldLoadPrimaryTabs ? <CategoriesTab /> : <TabPanelLoader />}
        </TabsContent>

        <TabsContent value="plugins" className="pt-4">
          {shouldLoadPrimaryTabs ? <PluginsTab /> : <TabPanelLoader />}
        </TabsContent>

        <TabsContent value="directory" className="pt-4">
          {shouldLoadPrimaryTabs ? <DirectoryTab /> : <TabPanelLoader />}
        </TabsContent>
        </Tabs>
      </div>

      <AddTransactionDrawer
        open={isAddTransactionOpen}
        onOpenChange={setIsAddTransactionOpen}
      />

      <DatePickerDialog
        open={isDatePickerOpen}
        onOpenChange={setIsDatePickerOpen}
        selectedDate={selectedDate}
        onDateChange={(date) => {
          setSelectedDate(date)
          setDateRange({ start: null, end: null })
        }}
        onDateRangeChange={(start, end) => {
          setDateRange({ start, end })
          if (start) setSelectedDate(start)
        }}
      />

        {/* Floating Action Button - AI Assistant with Voice Option */}
        <Button
          size="lg"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 h-14 px-6 rounded-full bg-gradient-to-br from-[#88a1ac] to-white/80 text-slate-950 shadow-lg shadow-[#88a1ac]/50 hover:shadow-xl hover:shadow-white/60 hover:scale-105 transition-all duration-200 border-2 border-white/20 flex items-center gap-3 [&_svg]:text-slate-950"
          aria-label="NinGenie AI Options"
        >
          {/* Audio Lines Icon - Opens Voice Interface */}
          <div
            onClick={(e) => {
              e.stopPropagation()
              window.location.href = '/ningenie/voice'
            }}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
            aria-label="Open NinGenie Voice Interface"
          >
            <AudioLines className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />
          </div>

          {/* Separator */}
          <div className="text-slate-950/60 font-mono text-lg leading-none">|</div>

          {/* Bot Icon - Opens Chat Drawer */}
          <div
            onClick={(e) => {
              e.stopPropagation()
              setIsChatDrawerOpen(true)
            }}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
            aria-label="Ask NinGenie AI Assistant"
          >
            {chatAnimationData && Lottie ? (
              <Lottie
                lottieRef={lottieRef}
                animationData={chatAnimationData}
                loop={false}
                autoplay={false}
                onComplete={() => {
                  // baby: when animation completes, go back to middle frame (thumbnail)
                  if (lottieRef.current) {
                    try {
                      lottieRef.current.goToAndStop(900, true)
                    } catch (error) {
                      console.error('[NinGenie] Failed to set final frame:', error)
                    }
                  }
                }}
                style={{ 
                  width: 21, 
                  height: 21,
                  filter: darkMode ? 'brightness(0) invert(1)' : 'none'
                }}
              />
            ) : (
              <Bot className="h-5 w-5" aria-hidden="true" />
            )}
          </div>
        </Button>

        {/* Sign In Drawer */}
        <SignInDrawer
          open={isSignInDrawerOpen}
          onOpenChange={setIsSignInDrawerOpen}
        />

        {/* AI Chat Drawer */}
        <NinGenieChatDrawer
          open={isChatDrawerOpen}
          onOpenChange={setIsChatDrawerOpen}
        />

        {/* Settings Drawer */}
        <SettingsDrawer
          open={isSettingsOpen}
          onOpenChange={setIsSettingsOpen}
        />

        {/* OAuth Success Dialog */}
        <Dialog open={showOAuthSuccessDialog} onOpenChange={setShowOAuthSuccessDialog}>
          <DialogContent className="sm:max-w-md bg-[var(--background)] z-[60]">
            <DialogHeader>
              <div className="flex items-center justify-center mb-4">
                <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <DialogTitle className="text-center text-xl font-semibold">
                {oauthSuccessType === 'calendar' ? 'Google Calendar Connected Successfully!' : 'Gmail Connected Successfully!'}
              </DialogTitle>
              <DialogDescription className="text-center pt-2">
                {emailParam && (
                  <p className="text-sm text-muted-foreground mb-3">
                    Connected to: <span className="font-medium text-foreground">{emailParam}</span>
                  </p>
                )}
                <p className="text-base font-medium text-foreground">
                  Please go back to the app now.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Your {oauthSuccessType === 'calendar' ? 'Google Calendar' : 'Gmail'} account has been connected and is ready to use.
                </p>
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                onClick={() => setShowOAuthSuccessDialog(false)}
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                Got it
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

const NinGenieHomeScreen = memo(NinGenieHomeScreenComponent)

NinGenieHomeScreen.displayName = 'NinGenieHomeScreen'

const NinGenieResponsiveScreen = () => {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false
    }
    return window.innerWidth >= 768
  })

  useEffect(() => {
    const updateLayout = () => setIsDesktop(window.innerWidth >= 768)

    if (typeof window.matchMedia === 'function') {
      const mediaQuery = window.matchMedia('(min-width: 768px)')
      updateLayout()
      mediaQuery.addEventListener('change', updateLayout)
      return () => {
        mediaQuery.removeEventListener('change', updateLayout)
      }
    }
    
    updateLayout()
    window.addEventListener('resize', updateLayout)
    return () => {
      window.removeEventListener('resize', updateLayout)
    }
  }, [])

  if (isDesktop) {
    return <NinGenieDesktopHomeScreen />
  }

  return <NinGenieHomeScreen />
}

// baby: Wrap in Suspense so useSearchParams inside NinGenieHomeScreenComponent is allowed during prerender
export default function NinGeniePage() {
  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Loading workspace...</div>}>
      <NinGenieResponsiveScreen />
    </Suspense>
  )
}
